import { getJSON, postJSON } from 'common/services/api'
import { isApiError } from 'common/services/apiError'
import {
  GET_INCIDENT_REPORT_BY_LOG_URL,
  incidentReportByLogQueryKey
} from 'hooks/useIncidentReport/useIncidentReportByLog'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useQueryClient } from 'react-query'

/**
 * A file on a follow-up action or the written follow-up, as the case read
 * returns it. Mirrors `IncidentCaseAttachment` in `@lmc/validations`; switch to
 * that once the package is bumped past the release that adds it.
 */
export type IncidentCaseAttachment = {
  /** Opaque; the handle for deleting the file. */
  key: string
  /** The file name as uploaded. */
  name: string
  mimetype?: string
  size?: number
  /** A signed link, or null when signing failed: listed, but not linked. */
  url: string | null
}

/** Where the files hang off the case. */
export type CaseAttachmentTarget =
  | { kind: 'action'; actionId: string }
  | { kind: 'writtenFollowUp' }

/** Saved files, and files picked but not uploaded until the step saves. */
export type AttachmentsValue = {
  saved: ReadonlyArray<IncidentCaseAttachment>
  queued: ReadonlyArray<File>
}

export const NO_ATTACHMENTS: AttachmentsValue = { saved: [], queued: [] }

/*
 * The upload limits the API enforces, mirrored from `@lmc/validations`
 * (INCIDENT_CASE_ATTACHMENT_MAX_BYTES, _MAX_FILES, _TYPES and
 * IncidentCaseAttachmentRejectionEnum). Import them from there once the
 * package is bumped past the release that adds them.
 */
export const MAX_FILE_MB = 20
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
/** Per upload; every queued file goes up in one request. */
export const MAX_FILES = 10
/** Allowed extensions, with the types a browser reports for them. */
const ALLOWED_TYPES: Record<string, ReadonlyArray<string>> = {
  doc: ['application/msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  eml: ['message/rfc822'],
  gif: ['image/gif'],
  heic: ['image/heic', 'image/heic-sequence'],
  heif: ['image/heif', 'image/heif-sequence'],
  jpeg: ['image/jpeg'],
  jpg: ['image/jpeg'],
  msg: ['application/vnd.ms-outlook'],
  pdf: ['application/pdf'],
  png: ['image/png'],
  webp: ['image/webp']
}
/** Why the API refused an upload, in a 400's `data.reason`. */
const REJECTION_KEYS: Record<string, string> = {
  FILE_TOO_LARGE: incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD_TOO_LARGE,
  FILE_TYPE_NOT_ALLOWED:
    incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD_TYPE_NOT_ALLOWED,
  TOO_MANY_FILES: incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD_TOO_MANY
}

/** The file picker's filter. Checked again on pick: it can be bypassed. */
export const ACCEPTED_FILES = [
  ...new Set(
    Object.entries(ALLOWED_TYPES).flatMap(([extension, types]) => [
      `.${extension}`,
      ...types
    ])
  )
].join(',')

const extensionOf = (name: string) =>
  name.includes('.') ? (name.split('.').pop()?.toLowerCase() ?? '') : ''

/** Why the files picked can't be added, as a translation, or null. */
export const pickedFilesProblem = (
  files: ReadonlyArray<File>,
  queuedCount: number
): { key: string; values: Record<string, string | number> } | null => {
  const wrongType = files.find((file) => !ALLOWED_TYPES[extensionOf(file.name)])
  if (wrongType) {
    return {
      key: incidentReportsConstants.CASE_V2_ATTACHMENTS_TYPE_NOT_ALLOWED,
      values: { name: wrongType.name }
    }
  }
  const tooLarge = files.find((file) => file.size > MAX_FILE_BYTES)
  if (tooLarge) {
    return {
      key: incidentReportsConstants.CASE_V2_ATTACHMENTS_TOO_LARGE,
      values: { name: tooLarge.name, size: MAX_FILE_MB }
    }
  }
  if (queuedCount + files.length > MAX_FILES) {
    return {
      key: incidentReportsConstants.CASE_V2_ATTACHMENTS_TOO_MANY,
      values: { count: MAX_FILES }
    }
  }
  return null
}

const targetUrl = (incidentReportId: string, target: CaseAttachmentTarget) =>
  target.kind === 'action'
    ? `list/incident-report/${incidentReportId}/actions/${target.actionId}/attachments`
    : `list/incident-report/${incidentReportId}/duty-of-candour/written-follow-up/attachments`

type AttachmentsResponse = {
  data: { attachments: Array<IncidentCaseAttachment> }
}

/** Uploads every file in one request; resolves to the target's files. */
export const uploadCaseAttachments = async (
  incidentReportId: string,
  target: CaseAttachmentTarget,
  files: ReadonlyArray<File>
) => {
  const body = new FormData()
  files.forEach((file) => {
    body.append('attachments', file, file.name)
  })
  const response: AttachmentsResponse = await postJSON({
    url: targetUrl(incidentReportId, target),
    method: 'PUT',
    contentType: 'multipart/form-data',
    body
  })
  return response.data.attachments
}

/** The most keys one delete request may carry. */
const MAX_KEYS_PER_DELETE = 100

/** A delete that failed after some batches had gone through. */
class PartialDeleteError extends Error {
  constructor(
    readonly attachments: Array<IncidentCaseAttachment>,
    readonly cause: unknown
  ) {
    super('Some attachments could not be deleted')
  }
}

/**
 * Deletes by key, at most 100 keys a request, as the API allows; resolves to
 * the target's files.
 */
export const deleteCaseAttachments = async (
  incidentReportId: string,
  target: CaseAttachmentTarget,
  keys: ReadonlyArray<string>
) => {
  let attachments: Array<IncidentCaseAttachment> | undefined
  for (let start = 0; start < keys.length; start += MAX_KEYS_PER_DELETE) {
    try {
      const response: AttachmentsResponse = await postJSON({
        url: targetUrl(incidentReportId, target),
        method: 'DELETE',
        json: { keys: keys.slice(start, start + MAX_KEYS_PER_DELETE) }
      })
      attachments = response.data.attachments
    } catch (error) {
      // Earlier batches went through, so what is left is known.
      throw attachments && !attachmentsLeftAfterFailedDelete(error)
        ? new PartialDeleteError(attachments, error)
        : error
    }
  }
  return attachments ?? []
}

/**
 * The target's files after a delete that failed part-way (a 502, or a later
 * batch failing): the ones still stored are listed. Undefined otherwise.
 */
export const attachmentsLeftAfterFailedDelete = (
  error: unknown
): Array<IncidentCaseAttachment> | undefined => {
  if (error instanceof PartialDeleteError) {
    return error.attachments
  }
  if (!isApiError(error) || error.status !== 502) {
    return undefined
  }
  const data = (error.responseBody as { data?: { attachments?: unknown } })
    ?.data
  return Array.isArray(data?.attachments)
    ? (data.attachments as Array<IncidentCaseAttachment>)
    : undefined
}

/**
 * The cached report, which the steps and the case view read. Uploads and
 * deletes change it behind the cache's back, so they refresh it.
 */
export const useCaseReportCache = (incidentReport: { log: { id: string } }) => {
  const queryClient = useQueryClient()
  // Read when needed: the log is only certain on a real report.
  const queryKey = () => incidentReportByLogQueryKey(incidentReport.log.id)

  return {
    /** The saved case read afresh, refreshing the cache with it. */
    readSavedCase: async () => {
      const response = await queryClient.fetchQuery<{
        data: { case?: unknown }
      }>(
        queryKey(),
        () =>
          getJSON({
            url: `${GET_INCIDENT_REPORT_BY_LOG_URL}/${incidentReport.log.id}`
          }),
        { staleTime: 0 }
      )
      return response.data.case
    },
    refreshReport: () => {
      queryClient.invalidateQueries(queryKey())
    }
  }
}

/** What to tell the user about a failed upload: the API's reason, if any. */
export const uploadFailure = (
  error: unknown
): { key: string; values?: Record<string, number> } => {
  const reason =
    isApiError(error) && error.status === 400
      ? (error.responseBody as { data?: { reason?: unknown } })?.data?.reason
      : undefined
  const key = typeof reason === 'string' ? REJECTION_KEYS[reason] : undefined
  return key
    ? { key, values: { count: MAX_FILES, size: MAX_FILE_MB } }
    : { key: incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD_FAILED }
}

/** The keys in `before` that are no longer in `after`. */
export const removedKeys = (
  before: ReadonlyArray<IncidentCaseAttachment>,
  after: ReadonlyArray<IncidentCaseAttachment>
) =>
  before
    .map(({ key }) => key)
    .filter((key) => !after.some((file) => file.key === key))

/** An action's saved files; absent until any are uploaded. */
export const actionAttachments = (action: object) =>
  (action as { attachments?: Array<IncidentCaseAttachment> }).attachments ?? []

/** The written follow-up's saved files; absent until any are uploaded. */
export const writtenFollowUpAttachments = (communication: object | undefined) =>
  (
    communication as
      | { writtenFollowUpAttachments?: Array<IncidentCaseAttachment> }
      | undefined
  )?.writtenFollowUpAttachments ?? []
